/**
 * Image optimization utility for improved performance
 * Provides lazy loading, preloading, and image optimization features
 */

// Static image registry to track loaded images
const imageRegistry = new Map();

/**
 * Preload an image to cache it for later use
 * @param {string} src - Image source URL
 * @returns {Promise} - Resolves when image is loaded
 */
export const preloadImage = (src) => {
  if (!src || typeof src !== 'string') return Promise.resolve();
  
  // If already preloaded, return the existing promise
  if (imageRegistry.has(src)) {
    return imageRegistry.get(src);
  }
  
  // Create a new promise for this image
  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve(src);
    };
    
    img.onerror = (error) => {
      console.error(`Error preloading image: ${src}`, error);
      imageRegistry.delete(src); // Remove failed loads
      reject(error);
    };
    
    img.src = src;
  });
  
  // Store in registry
  imageRegistry.set(src, promise);
  
  return promise;
};

/**
 * Preload multiple images at once
 * @param {Array<string>} sources - Array of image URLs
 * @returns {Promise} - Resolves when all images are loaded
 */
export const preloadImages = (sources = []) => {
  return Promise.all(sources.map(src => preloadImage(src)));
};

/**
 * Get a placeholder image for when the real image is loading
 * @param {number} width - Width of placeholder
 * @param {number} height - Height of placeholder
 * @param {string} color - Background color
 * @returns {string} - Data URL for placeholder image
 */
export const getPlaceholderImage = (width = 100, height = 100, color = '#E2E8F0') => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'%3E%3Crect width='${width}' height='${height}' fill='${color.replace('#', '%23')}'/%3E%3C/svg%3E`;
};

/**
 * Props interface for the optimized image component
 * @typedef {Object} OptimizedImageProps
 * @property {string} src - Image source URL
 * @property {string} alt - Alternative text
 * @property {number} [width] - Width of image
 * @property {number} [height] - Height of image
 * @property {string} [className] - CSS classes
 * @property {boolean} [lazy=true] - Whether to use lazy loading
 * @property {function} [onLoad] - On load callback
 * @property {function} [onError] - On error callback
 */

/**
 * Create an optimized image with lazy loading support
 * To use with React, implement a component that uses this function
 * 
 * @param {OptimizedImageProps} props - Image properties
 * @returns {HTMLImageElement} - Image element
 */
export const createOptimizedImage = ({
  src,
  alt,
  width,
  height,
  className = '',
  lazy = true,
  onLoad,
  onError
}) => {
  const img = document.createElement('img');
  
  img.alt = alt || '';
  if (width) img.width = width;
  if (height) img.height = height;
  if (className) img.className = className;
  
  // Add loading attribute if supported and lazy loading is requested
  if (lazy && 'loading' in HTMLImageElement.prototype) {
    img.loading = 'lazy';
  }
  
  // Use default placeholder while loading
  if (lazy) {
    img.src = getPlaceholderImage(width, height);
  }
  
  // Set up loading and error handlers
  img.onload = (event) => {
    if (onLoad) onLoad(event);
  };
  
  img.onerror = (event) => {
    console.error(`Failed to load image: ${src}`);
    if (onError) onError(event);
    
    // Use fallback image
    img.src = getPlaceholderImage(width, height, '#F87171');
  };
  
  // Set the actual source
  if (lazy) {
    // Use Intersection Observer for browsers that don't support native lazy loading
    if (!('loading' in HTMLImageElement.prototype)) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            img.src = src;
            observer.unobserve(img);
          }
        });
      });
      
      // Start observing the image
      if (img.parentElement) {
        observer.observe(img);
      } else {
        // If image is not yet in DOM, set up a MutationObserver
        const mutationObserver = new MutationObserver(() => {
          if (img.parentElement) {
            observer.observe(img);
            mutationObserver.disconnect();
          }
        });
        
        // Observe document for changes
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    } else {
      img.src = src;
    }
  } else {
    img.src = src;
  }
  
  return img;
};

// Export utility functions
const imageOptimizer = {
  preloadImage,
  preloadImages,
  getPlaceholderImage,
  createOptimizedImage
};

export default imageOptimizer;